/**
 * Session Cost Estimation Hook
 *
 * Fetches estimated credit cost for a session before generation.
 * Uses the backend /pricing/estimate-session-cost endpoint.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';

export interface SessionCostEstimate {
  credits_required: number;
  rate_per_minute: number;
  user_type: 'individual' | 'practitioner';
  tier: string;
  current_balance: number;
  can_afford: boolean;
  shortfall: number;
  duration_minutes: number;
  max_session_length_minutes: number;
  duration_exceeds_limit: boolean;
  balance_after_session: number | null;
}

interface UseSessionCostEstimateOptions {
  duration_minutes: number;
  enabled?: boolean;
}

/**
 * Hook to estimate session cost before generation
 *
 * @param options - Duration in minutes and optional enabled flag
 * @returns Cost estimate data and loading state
 *
 * @example
 * ```tsx
 * const { data: estimate, isLoading } = useSessionCostEstimate({
 *   duration_minutes: 10,
 *   enabled: true
 * });
 *
 * if (estimate && !estimate.can_afford) {
 *   // Show insufficient credits warning
 * }
 * ```
 */
export function useSessionCostEstimate({
  duration_minutes,
  enabled = true,
}: UseSessionCostEstimateOptions) {
  return useQuery({
    queryKey: ['session-cost-estimate', duration_minutes],
    queryFn: async (): Promise<SessionCostEstimate> => {
      return apiClient.post<SessionCostEstimate>('/pricing/estimate-session-cost', {
        activity_type: 'audio_generation',
        duration_minutes,
      });
    },
    enabled: enabled && duration_minutes > 0,
    staleTime: 30 * 1000, // 30 seconds - balance may change
    retry: 1,
  });
}

/**
 * Format credit cost for display
 */
export function formatCreditCost(estimate: SessionCostEstimate | undefined): string {
  if (!estimate) return '';

  const { credits_required, rate_per_minute, duration_minutes } = estimate;
  return `${credits_required} credits (${duration_minutes} min × ${rate_per_minute}/min)`;
}

/**
 * Get warning message if user can't afford session
 */
export function getCostWarningMessage(estimate: SessionCostEstimate | undefined): string | null {
  if (!estimate) return null;

  if (estimate.duration_exceeds_limit) {
    return `Session length (${estimate.duration_minutes} min) exceeds your tier limit of ${estimate.max_session_length_minutes} minutes.`;
  }

  if (!estimate.can_afford) {
    return `Insufficient credits. You need ${estimate.credits_required} but have ${estimate.current_balance}. Short by ${estimate.shortfall} credits.`;
  }

  return null;
}
